/**
 * Reducers update a specific prop in the application state, by operating
 * after the action which describes what to change.
 * To add a new action, add it to the switch statement in the dataReducer function
 *
 * @example
 * case NEW_ACTION_CONSTANT:
 *   return assign({}, state, {
 *     stateVariable: action.var,
 *   });
 *
 * To add a new reducer, add another file like this to the reducers folder, and
 * add it in rootReducer.js.
 */

import * as types from '../constants';
import { sheetParseConfig as c } from '../config/config';
import mori from 'mori';
import { getConfig, buildHeaders } from '../services/transducers';
import getDatacenterParentColumn from '../services/columnFilters';
import getDatacenterTimeframes from '../services/timeframeFilters';
import getDatacenterDataviews from '../services/dataviewFilters';
import getDatacenterDistributions from '../services/distributionFilters';
import buildMedicareData from './countyReducer';

// Set up an initial state to hold data. Use count to assign ids so we can
// remove items from the collection
const initialState = {
  items: [],
  count: 0,
};

export default function(state = initialState, action) {
  switch (action.type) {
    // Assign the next id and add the item to data collection
    case types.BUILD_DATA:
      return buildSpreadsheetData(
        action.data.data,
        action.data.options
      );
    default:
      return state;
  }
}

export function buildSpreadsheetData(data, options) {
  if (data.geoDefinitions) {
    return buildMedicareData(data);
  }

  let config = getConfig(data);
  const timeframes = getDatacenterTimeframes(data);
  const {
    immutableHeaders,
    immutableHeadersAll,
    hasDataviews,
  } = buildHeaders(data, config);

  const {
    immutableDataviews,
    immutableDataviewsAll,
  } = getDatacenterDataviews(
    immutableHeaders,
    config,
    immutableHeadersAll
  );
  const immutableData = buildData(data, immutableHeadersAll);

  config = mori.assocIn(
    config,
    ['settings', 'globalSettings', 'hasDataviews'],
    hasDataviews
  );
  if (timeframes.length < 2) {
    config = mori.assocIn(config, ['globalRules', 'Graph'], false);
  }

  const {
    immutableDistributions,
    immutableDistributionsAll,
  } = getDatacenterDistributions(immutableHeaders, [], immutableHeadersAll);

  const {
    columns,
    columnsAll,
  } = getDatacenterParentColumn(data, config, options);

  const metaData = Object.keys(immutableHeadersAll)
    .reduce((acc, curr) => ({
      [curr]: {
        immutableHeaders: immutableHeadersAll[curr].immutableHeaders,
        immutableDataviews: immutableDataviewsAll[curr].immutableDataviews,
        immutableDistributions:
          immutableDistributionsAll[curr].immutableDistributions,
        columns: columnsAll[curr],
      },
      ...acc,
    }), {});

  return {
    config,
    metaData,
    immutableHeaders,
    immutableData,
    immutableDataviews,
    immutableDistributions,
    // vanilla js objects for template binding
    columns,
    timeframes,
    globalRules: mori.toJs(
      mori.get(config, 'globalRules', mori.hashMap())
    ),
    globalSettings: mori.toJs(
      mori.getIn(config, ['settings', 'globalSettings'], mori.hashMap())
    ),
    // TODO:  dist count for all sheets
    distributionCount: mori.count(immutableDistributions),
  };
}

/**
 * ***************
 * Sheets are transduced into an arrays of objects with complex keys
 * so it's easier to filter by dataview and type
 * @example
 * [ 'js#{ "Location" "geography" }': 'Alaska', 'js#{ "Marketplace Type" "text"}': 'State' ]
 * ***************
 */
function buildData(data, headersAll) {
  // a set of header tuples to use as data keys
  const headerMap = (headerItems) => mori.mapcat(
    (header) => mori.vector(mori.set(
      mori.get(header, 'headerGroup') === null ? [
        mori.get(header, 'characteristic'),
        mori.get(header, 'label'),
      ] : [
        mori.get(header, 'headerGroup'),
        mori.get(header, 'characteristic'),
        // If label and char. are the same (ie. years - make the label unique)
        // Label/Char will be same if Doc uses only Year values (2016, etc.)
        // TODO: find a better way to handle this
        (mori.get(header, 'label') === mori.get(header, 'characteristic') ?
          `_${mori.get(header, 'label')}` : mori.get(header, 'label')),
      ]
    )),
    headerItems
  );

  return mori.into(
    mori.vector(),
    mori.map(
      (sheet) => mori.set([
        mori.first(sheet),
        mori.map(
          (row) => mori.zipmap(
            headerMap(getHeadersForSheet(headersAll, mori.first(sheet))),
            // Pass through everything except cell values that are explicitly
            // ints or floats. Everything else should be kept as a string.
            mori.map(
              (x) => (/^(-)?[\d]+(\.[\d]+)?[\s]?$/.test(x)) ?
                parseFloat(x) :
                x,
              row
            )
          ),
          mori.dropWhile((x) => !mori.first(x), mori.first(mori.rest(sheet)))
        ),
      ]),
      // remove config sheet
      mori.filter((x) => {
        return c.configTitles.indexOf(x[0].toLowerCase()) === -1;
      }, data)
    )
  );
}

function getHeadersForSheet(headers, sheetTitle) {
  const key = Object.keys(headers)
    .filter((headerKey) => headerKey === sheetTitle)[0];

  return headers[key].immutableHeaders;
}



// WEBPACK FOOTER //
// ../~/eslint-loader!./js/reducers/dataReducer.js